// Rung 2 of the disclosure ladder: the spreadsheet view of a lineup panel.
//
// Page templates show the same data different ways, and components switch
// from zen to spreadsheet style in one click. This is the spreadsheet half.
// Every column is here at once, aligned, because the reason to want this
// view is comparing rows against each other — and nobody can compare six
// players' averages by hovering each one in turn.
//
// It is the same rows and the same numbers as the zen view; nothing is added
// and nothing is judged. The panel's switch decides which one you see, and the
// detail level decides which pole the panel opens at.
//
// THE NUMBERS FIT THE PANEL, NOT THE OTHER WAY ROUND (round 3, N6). A panel
// narrower than the whole table — a phone, a tablet's half, the desktop's
// bench — keeps Slot, Player, Proj, Pts and Avg as columns and folds the
// fixture, the status and the row's words under the name, so the three
// numbers the table exists for are on screen without scrolling sideways. At
// 390 they sat behind a sideways scroll, and at 1280 the bench's Avg was off
// the panel's edge. A container query decides, so the panel's own width is
// what counts, not the window's.
//
// ONE TABLE, ONE HEADER, A BAND PER GROUP (B-215). The starters and the bench
// are groups of one table, each under a tinted band, with the column names
// once at the top. Two tables stacked repeated the header under "Bench", which
// added nothing.
//
// THE COLUMN NAMES STAY IN SIGHT (UX-36). The head sticks to the top of
// whatever scrolls the table, and a move's line rides in it, so neither the
// names nor the move's Cancel scrolls away under a long bench.
//
// THE BADGE MOVES, THE NAME OPENS THE CARD (B-275). On My Team the Slot
// column is the position badge as a button, which starts a move from any row
// and ends one on a highlighted row, as in the list view; the name is the
// player, and opens his card. A tap elsewhere on the row is a bigger target
// for the same things, and the screen decides what it does.
//
// THE ROW'S OWN BUTTON CHOOSES ITS SLOT (B-320): at the row's end on My Team,
// for a keyboard and a screen reader, what a press elsewhere on the row does
// for a pointer — who can fill it, beside the lineup.

import type { HTMLAttributes, ReactNode } from 'react';
import type { LineupRowView } from '@fsp/domain';
import { useReaderZone } from '../../data/hooks';
import { positionLabel, ProTeamCode, SlotButton, StatusBadge } from '../../kit';
import { Chip, IconButton } from '../../kit/ui';
import { Icon } from '../../kit/Icon';
import { lockWords } from './lock';
import { ProjectionWhy, projectionNote } from './Projection';
import { PointsWhy } from './PointsWhy';
import { TableScroll } from '../../kit/TableScroll';
import { Term } from '../../kit/Why';
import { GLOSSARY } from './glossary';
import { num } from './notComputed';
import { MatchupChip } from './MatchupChip';
import { clubLabel } from '../../sports/registry';
import { useTools } from '../../data/tools';

function opponent(row: LineupRowView): string {
  if (!row.game) return '—';
  if (row.game.state === 'bye') return 'Bye';
  if (!row.game.opponent) return '—';
  return `${row.game.home ? 'vs' : 'at'} ${clubLabel(row.game.opponent)}`;
}

/** One group of the table: its band's words and its rows. */
export interface LineupSheetGroup {
  label: string;
  rows: LineupRowView[];
  /** A line under the band's label: the league's IR rule on the Reserve band (D9). */
  note?: string | null;
}

/** The Slot column's badge as a button, on a team the reader can change (B-275). */
export interface LineupSheetSlot {
  onPress: (row: LineupRowView) => void;
  /** What a press does, after the slot's words: "move Brock Purdy". */
  action: (row: LineupRowView) => string;
  state: (row: LineupRowView) => 'picked' | 'target' | 'locked' | null;
  /** The press in a word beside the badge where there is room (B-288); none on a phone. */
  word?: (row: LineupRowView) => string | null;
  /** A desktop's drag (B-288): a row's handlers, none where a row cannot be dragged. */
  drag?: (row: LineupRowView) => DragProps;
  /** The row a drag is over, as `slot-slotIndex`. */
  over?: string | null;
}

/** A row's slot as a thing to choose (B-320): its button at the row's end, and which is chosen. */
export interface LineupSheetFill {
  label: (row: LineupRowView) => string;
  chosen: (row: LineupRowView) => boolean;
  /** Asleep while a move is open, whose rows are the move's. */
  disabled: boolean;
  onPress: (row: LineupRowView) => void;
}

/** What a row needs to be dragged and dropped on (`LineupScreen`'s `dragProps`). */
export type DragProps = Pick<
  HTMLAttributes<HTMLElement>,
  'draggable' | 'onDragStart' | 'onDragOver' | 'onDrop' | 'onDragEnd'
>;

/** Every column the table has, for a band that spans them, less the slot's button. */
const COLUMNS = 8;

export function LineupSheet({
  groups,
  onRow,
  onName,
  rowState,
  slot,
  shaking = null,
  banner = null,
  fill,
}: {
  groups: LineupSheetGroup[];
  /** A tap elsewhere on a row: the same as the list view's, so the switch never costs an action. */
  onRow: (row: LineupRowView) => void;
  /** The player's name: his card, or the end of a move in progress (B-204). */
  onName: (row: LineupRowView) => void;
  /**
   * Where a move in progress puts this row, as the list view marks it: the
   * row picked, one it can trade places with, or one it cannot. The table
   * showed none of it once, so a tap there started a swap nobody could see
   * (round 3, N6).
   */
  rowState?: (row: LineupRowView) => 'selected' | 'eligible' | 'dim' | null;
  /** The badge as a button; absent on another manager's team. */
  slot?: LineupSheetSlot;
  /** The row a tap on a lock is shaking (`slot-slotIndex`), B-221. */
  shaking?: string | null;
  /** A move's line, in the head over the column names (UX-36). */
  banner?: ReactNode;
  /** The slot's button at each row's end; absent on another manager's team. */
  fill?: LineupSheetFill;
}) {
  const zone = useReaderZone();
  // No Proj column in a league that keeps no projections (RULES_SCHEMA §15c).
  const tools = useTools();
  const columns = COLUMNS + (fill ? 1 : 0) - (tools.projections ? 0 : 1);
  return (
    <TableScroll className="fsp-sheet-wrap">
      <table className="fsp-sheet">
        <thead>
          {banner ? (
            <tr className="fsp-sheet-banner">
              <td colSpan={columns}>{banner}</td>
            </tr>
          ) : null}
          <tr>
            {/* Every short label says what it means when asked (the
                page-by-page review of 2026-10-11, UX_STANDARDS UX-25). */}
            <th scope="col">
              <Term tip={GLOSSARY.slot}>Slot</Term>
            </th>
            <th scope="col">Player</th>
            <th scope="col" className="fsp-sheet-wide">
              <Term tip={GLOSSARY.position}>Pos</Term>
            </th>
            <th scope="col" className="fsp-sheet-wide">
              <Term tip={GLOSSARY.opponent}>Opp</Term>
            </th>
            <th scope="col" className="fsp-sheet-wide">
              <Term tip={GLOSSARY.status}>Status</Term>
            </th>
            {tools.projections ? (
              <th scope="col" className="fsp-sheet-num">
                <Term tip={GLOSSARY.weekProjection}>Proj</Term>
              </th>
            ) : null}
            <th scope="col" className="fsp-sheet-num">
              <Term tip={GLOSSARY.weekPoints}>Pts</Term>
            </th>
            <th scope="col" className="fsp-sheet-num">
              <Term tip={GLOSSARY.seasonAverage}>Avg</Term>
            </th>
            {fill ? (
              <th scope="col" className="fsp-sheet-fill">
                <span className="fsp-sr">Who can fill it</span>
              </th>
            ) : null}
          </tr>
        </thead>
        {groups.map(({ label, rows, note }) => (
          <tbody key={label}>
            <tr className="fsp-sheet-group">
              <th scope="colgroup" colSpan={columns}>
                {label}
                {note ? (
                  <div className="fsp-sub fsp-sheet-note fsp-ui-wrap-text">{note}</div>
                ) : null}
              </th>
            </tr>
            {rows.map((r) => {
              const note = projectionNote(r.projection, r.basis);
              const onBye = r.game?.state === 'bye';
              return (
                <tr
                  key={`${r.slot}-${r.slotIndex}`}
                  className={`fsp-sheet-row${rowState?.(r) ? ` fsp-sheet-row-${rowState(r)}` : ''}${shaking === `${r.slot}-${r.slotIndex}` ? ' fsp-shake' : ''}${slot?.over === `${r.slot}-${r.slotIndex}` ? ' fsp-sheet-row-over' : ''}`}
                  {...(slot?.drag?.(r) ?? {})}
                  data-locked={(r.player !== null && r.locked) || undefined}
                  data-chosen={fill?.chosen(r) || undefined}
                  // A BIGGER TARGET, NOT A CONTROL OF ITS OWN (B-275): the badge
                  // and the name are the row's buttons, for a keyboard and a
                  // screen reader; it is no longer a focus stop of its own, so
                  // Enter cannot both open a card and start a move (SCREENS-1).
                  data-ux-convenience=""
                  // `s` and `b` press the badge, and Enter the name, when a
                  // key brings focus here (M5, `shell/keyboardMap.tsx`).
                  data-kbd-move={slot ? (r.slotKind === 'starter' ? 'b' : 's') : undefined}
                  onClick={() => onRow(r)}
                >
                  <td className="fsp-cap fsp-sheet-slot">
                    {slot ? (
                      <SlotButton
                        pos={r.slot}
                        size="sm"
                        action={slot.action(r)}
                        state={slot.state(r)}
                        word={slot.word?.(r) ?? null}
                        onClick={() => slot.onPress(r)}
                      />
                    ) : (
                      r.slot
                    )}
                  </td>
                  <td className="fsp-sheet-name">
                    {r.player ? (
                      // THE PADLOCK BEFORE THE NAME, ON ITS LINE (B-221): after
                      // the name it wrapped under it and drew every locked row
                      // two lines high.
                      <div className="fsp-sheet-who">
                        {r.locked ? (
                          // The table's lock, as the list's: the time in the
                          // status column when the table has one, and on the
                          // padlock for a screen reader and a pointer.
                          <Term className="fsp-lock" tip={lockWords(r, zone)} name="">
                            <Icon name="locked" size="sm" />
                          </Term>
                        ) : null}
                        <button
                          type="button"
                          // The whole name, where a narrow table cuts it (UX-11).
                          title={r.player.name}
                          className="fsp-linklike fsp-sheet-player"
                          onClick={(e) => {
                            e.stopPropagation();
                            onName(r);
                          }}
                        >
                          {r.player.name}
                        </button>
                        {/* HIS CLUB BESIDE HIS NAME, as every row has it (B-510),
                            in place of a Team column of its own; folded under
                            it, before the fixture, where the table is narrow
                            and the name needs the line. */}
                        <span className="fsp-sheet-club">
                          <ProTeamCode code={r.player.proTeam} />
                        </span>
                        {/* WHO COUNTS IN BEST BALL (Q2, B-363), as the list says. */}
                        {r.countsAs !== null ? (
                          <Chip tone="accent">
                            {r.countsAs === r.slot ? 'Counts' : `Counts at ${r.countsAs}`}
                          </Chip>
                        ) : null}
                      </div>
                    ) : (
                      'Empty'
                    )}
                    {r.player ? (
                      // Folded under the name when the panel is narrow; hidden
                      // when the table has the room for its own columns.
                      <div className="fsp-sheet-inline">
                        <div className="fsp-sub">
                          <ProTeamCode code={r.player.proTeam} /> {opponent(r)}{' '}
                          <StatusBadge player={{ ...r.player, onBye: false }} />
                        </div>
                        {note !== null && !onBye ? (
                          <div className="fsp-sub fsp-muted fsp-sheet-note">{note}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="fsp-sheet-wide">
                    {r.player?.positions[0] ? positionLabel(r.player.positions[0]) : '—'}
                  </td>
                  <td className="fsp-sheet-wide">
                    {opponent(r)}
                    {r.player && r.game?.state === 'pre' ? (
                      <MatchupChip
                        cue={r.matchup}
                        position={r.player.positions[0]}
                        opponent={r.game.opponent}
                      />
                    ) : null}
                  </td>
                  <td className="fsp-sheet-wide fsp-sheet-status">
                    {/* A BYE ONCE, IN THE GAME'S COLUMN (B-521): it read "Bye
                        BYE On bye" across one row. His injury still shows. */}
                    {r.player ? <StatusBadge player={{ ...r.player, onBye: false }} /> : null}
                    {/* "Locked", and the time on the padlock beside the name: a
                        time already past wrapped the cell to four lines
                        (the page-by-page review of 2026-10-11). */}
                    {r.player && r.locked ? (
                      <span className="fsp-muted fsp-sheet-note">Locked</span>
                    ) : null}
                    {/* The row's words, at every level (B-165): why there is no
                      number, or why the number is a stated 0. The sheet is what
                      My Team opens at Everything, and it showed a bare dash and a
                      bare 0.0 with the reason only in the popover (SCREENS-2). */}
                    {note !== null && !onBye ? (
                      <span className="fsp-muted fsp-sheet-note">{note}</span>
                    ) : null}
                  </td>
                  {tools.projections ? (
                    <td className="fsp-sheet-num fsp-num">
                      <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
                        {num(r.projection)}
                      </ProjectionWhy>
                    </td>
                  ) : null}
                  <td className="fsp-sheet-num fsp-num">
                    {r.points !== null ? (
                      <PointsWhy
                        player={r.player}
                        points={r.points}
                        breakdown={r.breakdown}
                        statLine={r.statLine}
                      >
                        {r.points.toFixed(1)}
                      </PointsWhy>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="fsp-sheet-num fsp-num">
                    {num(r.seasonAvg ?? r.basis?.seasonAvg ?? null)}
                  </td>
                  {fill ? (
                    <td className="fsp-sheet-fill">
                      <IconButton
                        icon="next"
                        variant="quiet"
                        size="sm"
                        label={fill.label(r)}
                        aria-pressed={fill.chosen(r)}
                        disabled={fill.disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          fill.onPress(r);
                        }}
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </TableScroll>
  );
}
